import { safeExternalImageSrc, safeInternalPath, safeUrl } from '../csp/url'

/** Hosts that CMS links may point to, each including its subdomains. */
const MORPHO_LINK_DOMAINS: ReadonlyArray<string> = ['morpho.org']

/** `morpho.org` or one of its subdomains. Lookalikes such as `morpho.org.evil.com` do not match. */
function isMorphoHost(hostname: string): boolean {
  return MORPHO_LINK_DOMAINS.some(domain => hostname === domain || hostname.endsWith(`.${domain}`))
}

/**
 * Returns a link target that is safe to render from CMS content: an in-app
 * path or an `https` URL on a Morpho domain. Anything else returns
 * `undefined`, and the caller renders plain text or no button.
 */
export function safeLinkHref(raw: string | null | undefined): string | undefined {
  if (typeof raw !== 'string') return undefined
  const trimmed = raw.trim()

  const internal = safeInternalPath(trimmed)
  if (internal) return internal

  const href = safeUrl(trimmed, { allowedProtocols: ['https:'] })
  if (!href) return undefined
  const url = new URL(href)
  if (url.username || url.password) return undefined
  return isMorphoHost(url.hostname) ? href : undefined
}

/**
 * Returns the media URL only when its origin is exactly `mediaOrigin` (the CMS
 * Blob store). An unset, malformed or non-`https` `mediaOrigin` fails closed.
 */
export function safeMediaUrl(
  raw: string | null | undefined,
  mediaOrigin: string | null | undefined
): string | undefined {
  if (!mediaOrigin) return undefined
  let expectedOrigin: string
  try {
    const expected = new URL(mediaOrigin)
    // Matches the CSP rule: a non-https store is never allowed, so neither side can be `http:`.
    if (expected.protocol !== 'https:') return undefined
    expectedOrigin = expected.origin
  } catch {
    return undefined
  }

  const src = safeExternalImageSrc(raw)
  if (!src) return undefined
  const url = new URL(src)
  if (url.username || url.password) return undefined
  return url.origin === expectedOrigin ? src : undefined
}
